// src/pages/ProductDetail.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { useDispatch } from "react-redux";

import ReactImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";

import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

import Layout from "../components/layoutBlack";
import { config } from "../utils/constants";
import { addToCart } from "../store/slices/cartSlice.ts";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import Breadcrumbs from "../components/breadcrumbs";
import DOMPurify from "dompurify";
import PromoOffer from "../components/promoOffer";
import { useRandomScrollPromo } from "../hooks/useRandomScrollPromo";
import { Helmet } from "react-helmet-async";


const ProductDetail = () => {
  const REACT_APP_API_URL = config.url.REACT_APP_API_URL;
  const REACT_API_IMAGE_URL = config.url.REACT_API_IMAGE_URL;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { paramSlug } = useParams(); // variant slug (string/number)
  const { state } = useLocation(); // optional: { familySlug }
  const passedSlug = state?.familySlug || null;
  const id = state?.product_id || null;

  const [family, setFamily] = useState(null);          // family payload
  const [selectedId, setSelectedId] = useState(null);  // string variant id
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);

  // Lightbox state
  const [lbOpen, setLbOpen] = useState(false);
  const [lbIndex, setLbIndex] = useState(0);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const increment = () => setQuantity((q) => q + 1);
  const decrement = () => setQuantity((q) => (q > 1 ? q - 1 : 1));
  const [promoOpen, setPromoOpen] = useState(false);

  // Hook will call this to open randomly
  const onTrigger = useCallback(() => setPromoOpen(true), []);

  useRandomScrollPromo({
    onTrigger,
    // Tune these as you like:
    minTimeMs: 10000,           // wait at least 8s on page
    minScrollPx: 300,           // and 800px of scroll
    probability: .8,          // 80% chance when we check
    checkThrottleMs: 1500,      // throttle checks on scroll
    cooldownMs: 3 * 60 * 1000, // 3 min cooldown
    dailyCap: 2,                // once per day
    sessionCap: 1,              // once per session
    storageKeyBase: "promo_random_product_detail",
  });

  const isLocalDevHost = (host) =>
    /^(localhost|127\.0\.0\.1|0\.0\.0\.0|.+\.local|.+\.test)$/i.test(host || "");

  const toHttpsIfNeeded = (u) => {
    try {
      const url = new URL(u);
      const pageIsHttps = typeof window !== "undefined" && window.location.protocol === "https:";
      if (pageIsHttps && url.protocol === "http:" && !isLocalDevHost(url.hostname)) {
        url.protocol = "https:";
      }
      return url.toString();
    } catch {
      return u;
    }
  };

  const joinPath = (base, path) => {
    const b = String(base || "").replace(/\/+$/, "");
    const p = String(path || "").replace(/^\/+/, "");
    return `${b}/${p}`;
  };

  const asUrl = (path) => {
    if (!path) return "/default.jpg";

    // Absolute URL: maybe upgrade depending on host
    if (/^https?:\/\//i.test(path)) {
      return toHttpsIfNeeded(path);
    }

    // Relative path: build from your image base
    const base = String(REACT_API_IMAGE_URL || "").replace(/\/+$/, "");
    const rel  = String(path).replace(/^\/+/, "");
    return toHttpsIfNeeded(`${base}/${rel}`);
  };

    const getVideoSrc = (sec) => {
    const u = sec?.video_url || sec?.video_file || "";
    return u ? asUrl(u) : "";
  };

  
  const renderSectionMedia = (sec, { className = "", style = {} } = {}) => {
    if (!sec) return null;

    if (sec.media_type === "video") {
      const src = getVideoSrc(sec);
      if (!src) return null;

      return (
        <video
          className={className}
          style={style}
          controls
          playsInline
          preload="metadata"
          poster={sec.poster ? asUrl(sec.poster) : undefined}
        >
          <source src={src} />
          Your browser does not support the video tag.
        </video>
      );
    }

    if (sec.image) {
      return (
        <img
          className={className}
          style={style}
          src={asUrl(sec.image)}
          alt={sec.image_alt || ""}
          loading="lazy"
        />
      );
    }

    return null;
  };

  const renderContentSection = (sec) => {
    const type = sec.section_type;
    const titleAboveMedia = (sec.media_title || "").trim();
    // sanitize rich text coming from CKEditor
    const safeBody = DOMPurify.sanitize(sec.body || "");
    const safeOverlayBody = DOMPurify.sanitize(sec.overlay_body || "");

    if (type === "standard-text") {
      return (
        <div key={sec.id} className="molu-section molu-standard-text">
          {sec.text_header ? <h4 className="molu-section-title">{sec.text_header}</h4> : null}
          {safeBody ? (
            <div className="molu-section-body" dangerouslySetInnerHTML={{ __html: safeBody }} />
          ) : null}
          {sec.cta_text && sec.cta_url ? (
            <a className="btn btn-dark mt-3" href={sec.cta_url}>
              {sec.cta_text}
            </a>
          ) : null}
        </div>
      );
    }

    if (type === "image-text") {
      return (
        <div key={sec.id} className="molu-section molu-image-text">
          {titleAboveMedia ? <h4 className="molu-section-title">{titleAboveMedia}</h4> : null}

          <div className="molu-media-wrap">
            {renderSectionMedia(sec, { className: "molu-media" })}
          </div>

          {sec.text_header ? <h5 className="molu-section-subtitle">{sec.text_header}</h5> : null}
          {safeBody ? (
            <div className="molu-section-body" dangerouslySetInnerHTML={{ __html: safeBody }} />
          ) : null}

          {sec.cta_text && sec.cta_url ? (
            <a className="btn btn-dark mt-3" href={sec.cta_url}>
              {sec.cta_text}
            </a>
          ) : null}
        </div>
      );
    }

    if (type === "left-image-with-text") {
      return (
        <div key={sec.id} className="molu-section molu-left-media-with-text">
          <div className="row g-3 align-items-start">
            <div className="col-md-5">
              <div className="molu-media-wrap">
                {renderSectionMedia(sec, { className: "molu-media" })}
              </div>
            </div>
            <div className="col-md-7">
              {sec.text_header ? <h4 className="molu-section-title">{sec.text_header}</h4> : null}
              {safeBody ? (
                <div className="molu-section-body" dangerouslySetInnerHTML={{ __html: safeBody }} />
              ) : null}
              {sec.cta_text && sec.cta_url ? (
                <a className="btn btn-dark mt-3" href={sec.cta_url}>
                  {sec.cta_text}
                </a>
              ) : null}
            </div>
          </div>
        </div>
      );
    }

if (type === "standard-image-dark-overlay" || type === "standard-image-light-overlay") {
  const overlayIsDark = type === "standard-image-dark-overlay";
  const overlayEnabled = sec.use_overlay !== false; // default true if missing
  const titleAboveMedia = (sec.media_title || "").trim();

    return (
      <div
        key={sec.id}
        className={`molu-section molu-overlay ${overlayIsDark ? "molu-overlay-dark" : "molu-overlay-light"}`}
      >
        {/* Optional title above the image/video */}
        {titleAboveMedia ? <h4 className="molu-section-title">{titleAboveMedia}</h4> : null}

        <div className="molu-overlay-media">
          {renderSectionMedia(sec, { className: "molu-overlay-img" })}

          {/* Optional overlay box */}
          {overlayEnabled && (
            <div className="molu-overlay-box">
              {sec.overlay_header ? <h4 className="m-0">{sec.overlay_header}</h4> : null}

              {safeOverlayBody ? (
                <div className="mt-2" dangerouslySetInnerHTML={{ __html: safeOverlayBody }} />
              ) : null}

              {sec.cta_text && sec.cta_url ? (
                <a
                  className={`btn mt-3 ${overlayIsDark ? "btn-light" : "btn-dark"}`}
                  href={sec.cta_url}
                >
                  {sec.cta_text}
                </a>
              ) : null}
            </div>
          )}
        </div>
      </div>
    );
  }

    return null;
  };

  // Load: single call if slug passed from Shop; fallback to 2 calls on refresh
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        if (passedSlug) {
          const fam = await axios
            .get(`${REACT_APP_API_URL}/products/families/${passedSlug}/`)
            .then((r) => r.data);
          if (!mounted) return;
          setFamily(fam);
          setSelectedId(String(id));
        } else {
          // const variant = await axios
          //   .get(`${REACT_APP_API_URL}/products/${paramSlug}/`)
          //   .then((r) => r.data);
          //   console.log(variant);
          // if (!mounted) return;
          const fam = await axios
            .get(`${REACT_APP_API_URL}/products/families/${paramSlug}/`)
            .then((r) => r.data);
          if (!mounted) return;
          setFamily(fam);
          setSelectedId(String(fam.default_variant_id));
        }
      } catch (err) {
        console.error("Error fetching product family:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [REACT_APP_API_URL, id, passedSlug]);

  // Current variant
  const variant = useMemo(() => {
    if (!family || !selectedId) return null;
    return family.variants?.[String(selectedId)] || null;
  }, [family, selectedId]);

  // Gallery items from variant media (images + optional video posters)
  const galleryItems = useMemo(() => {
    if (!variant) return [];
    const media = Array.isArray(variant.media) ? variant.media : [];
    const items = media.map((m) => {
      if (m.media_type === "image" && m.image) {
        const src = asUrl(m.image);
        return { type: "image", original: src, thumbnail: src, description: m.alt || "" };
      }
      if (m.poster) {
        const p = asUrl(m.poster);
        return { type: "poster", original: p, thumbnail: p, description: m.alt || "Video" };
      }
      return null;
    }).filter(Boolean);
    return items.length ? items : [{ type: "image", original: "/default.jpg", thumbnail: "/default.jpg", description: "" }];
  }, [variant]);

  // Slides for Lightbox (images/posters only)
  const lightboxSlides = useMemo(
    () => (galleryItems || []).map((it) => ({ src: it.original, alt: it.description || family?.name || "" })),
    [galleryItems, family?.name]
  );

  // Open lightbox at current gallery index
  const openLightboxAt = (i) => {
    setLbIndex(i);
    setLbOpen(true);
  };

  // Custom item renderer: click image => open lightbox; posters just display
  const renderItem = (item) => {
    if (item.type !== "image") {
      return (
        <div className="gallery-image" style={{ display: "grid", placeItems: "center" }}>
          <img
            className="image-gallery-image"
            src={item.original}
            alt={item.description || ""}
            style={{ maxWidth: "100%", maxHeight: "80vh", width: "auto", height: "auto", objectFit: "contain" }}
          />
        </div>
      );
    }
    const open = (e) => { e.stopPropagation(); openLightboxAt(galleryIndex); };
    return (
      <div className="gallery-image" onClick={open} style={{ display: "grid", placeItems: "center" }}>
        <img
          className="image-gallery-image"
          src={item.original}
          alt={item.description || family?.name || ""}
          style={{ maxWidth: "100%", maxHeight: "80vh", width: "auto", height: "auto", objectFit: "contain", cursor: "zoom-in" }}
        />
      </div>
    );
  };

  // Optional custom thumb (untouched, safe)
  const renderThumbInner = (item) => (
    <div style={{ position: "relative" }}>
      <img src={item.thumbnail} alt={item.description || ""} />
    </div>
  );

  // HTML description (variant first, fallback to family)
  const descriptionHTML = useMemo(() => {
    const html = variant?.description || family?.description || "";
    return DOMPurify.sanitize(html ?? "");
  }, [variant, family]);

  const handleAddToCart = () => {
    if (!family || !variant) return;
    const m = variant.media || [];
    const firstImg = m.find((x) => x.media_type === "image" && x.image)?.image;
    const firstPoster = m.find((x) => x.poster)?.poster;
    const thumb = asUrl(firstImg || firstPoster || "/default.jpg");

    dispatch(
      addToCart({
        id: variant.id,
        sku: variant.sku,
        name: family.name,
        description: variant.description || family.description || "",
        price: Number(variant.price || 0),
        image: thumb,
        quantity,
        option: variant.product_size || "",
      })
    );

    Swal.fire({
      icon: "success",
      html: `<p style="padding-bottom: 2rem; font-size: 1.1rem;">"${family.name}" added to cart!</p>`,
      showCloseButton: true,
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
      customClass: { popup: "rounded shadow", title: "text-center" },
    });
  };

  if (loading || !family || !variant) return <div>Loading...</div>;
  // --- SEO helpers ---
  const stripHtml = (html) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = String(html);
    return (div.textContent || div.innerText || "").trim();
  };
  const truncate = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s || "");

  // --- pick meta fields with graceful fallbacks ---
  const productName = family?.name || variant?.name || "Product";
  const sizeLabel = variant?.product_size ? ` – ${variant.product_size}` : "";
  const defaultTitle = `${productName}${sizeLabel} | Molu`;
  const rawDesc =
    variant?.meta_description ||
    variant?.description ||
    family?.meta_description ||
    family?.description ||
    "";
  const metaTitle =
    (variant?.meta_title && variant.meta_title.trim()) ||
    (family?.meta_title && family.meta_title.trim()) ||
    defaultTitle;

  const metaDescription = truncate(stripHtml(rawDesc), 300);
  const metaKeywords =
    (variant?.meta_keywords && variant.meta_keywords.trim()) ||
    (family?.meta_keywords && family.meta_keywords.trim()) ||
    [
      productName,
      variant?.product_size,
      variant?.sku,
      "GHK-Cu peptides", 
      "AHK-Cu peptides",
      "copper peptides for hair growth", 
      "copper peptide scalp serum", 
      "2dDR for hair growth", 
      "peptide hair care", 
      "peptide skin care", 
      "ionic peptide delivery complex",
      "hair growth",
      "copper peptides",
      "2-deoxy-d-ribose",
    ]
      .filter(Boolean)
      .join(", ");

  // canonical that is stable per variant id (adjust to your real public URL)
  const siteOrigin =
    typeof window !== "undefined" ? window.location.origin : "https://molu.co";
  const canonical = `${siteOrigin}/product/${paramSlug}`;

  // hero image for OG/Twitter
  const firstImageUrl = (() => {
    const m = variant?.media || [];
    const firstImg = m.find((x) => x.media_type === "image" && x.image)?.image;
    const firstPoster = m.find((x) => x.poster)?.poster;
    return asUrl(firstImg || firstPoster || "/default.jpg");
  })();

  // JSON-LD stays factual; it doesn’t need meta_* fields
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: productName,
    image: [firstImageUrl],
    sku: variant?.sku || undefined,
    brand: { "@type": "Brand", name: "Molu" },
    description: metaDescription,
    offers: {
      "@type": "Offer",
      priceCurrency: "USD",
      price: Number(variant?.price || 0).toFixed(2),
      availability: "https://schema.org/InStock",
      url: canonical,
    },
  };

  return (
    <div>
      <Layout>
        <Helmet prioritizeSeoTags>
          <title>{metaTitle}</title>
          <meta key="desc" name="description" content={metaDescription} />
          <meta name="description" content={metaDescription} />
          <meta name="keywords" content={metaKeywords} />
          <link rel="canonical" href={canonical} />

          {/* Open Graph */}
          <meta property="og:type" content="product" />
          <meta property="og:title" content={metaTitle} />
          <meta property="og:description" content={metaDescription} />
          <meta property="og:image" content={firstImageUrl} />
          <meta property="og:url" content={canonical} />

          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={metaTitle} />
          <meta name="twitter:description" content={metaDescription} />
          <meta name="twitter:image" content={firstImageUrl} />

          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        </Helmet>
      <section className="bg0 empty-top" >

      </section>
        <div className="container p-t-10">
          <Breadcrumbs />
        </div>

        <section className="full-page-section bg0 p-t-42 p-b-20">
          <div className="container">
            <div className="row isotope-grid">
              <section className="py-5 border-bottom">
                <div className="row">
                  {/* Media + Blurb */}
                  <div className="col-lg-6 mb-4">
                    <div style={{ maxWidth: "90%" }}>
                      <ReactImageGallery
                        key={selectedId}
                        items={galleryItems}
                        thumbnailPosition="left"
                        showFullscreenButton={false}
                        showPlayButton={false}
                        renderItem={renderItem}
                        renderThumbInner={(item) => (
                          <img
                            className="image-gallery-thumbnail-image"   // <- important
                            src={item.thumbnail}
                            alt={item.description || ""}
                            loading="lazy"
                          />
                        )}
                        onSlide={(i) => setGalleryIndex(i)}
                        additionalClass="molu-gallery"                 // <- scope for CSS below
                      />

                      {(family?.blurb || variant?.blurb) && (
                        <div className="blurb-card mt-4">
                          <p className="m-0">{family?.blurb || variant?.blurb}</p>
                        </div>
                      )}
                      {/* Product Content Sections (below gallery) */}
                      {Array.isArray(variant?.content_sections) && variant.content_sections.length > 0 && (
                        <div className="mt-4 molu-content-sections">
                          {variant.content_sections
                            .slice()
                            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.id ?? 0) - (b.id ?? 0))
                            .map(renderContentSection)}
                        </div>
                      )}

                    </div>
                  </div>

                  {/* Product Details */}
                  <div className="col-lg-6">
                    <h2 className="pt-3 h4 fw-bold">{family.name}</h2>

                    <p className="mt-4 fw-bold">
                      Availability: <span className="text-success">In Stock</span>
                    </p>
                    <p className="fw-bold">
                      SKU: <span className="fw-normal">{variant.sku}</span>
                    </p>

                    <p className="mt-3 display-6 text-primary">
                      ${Number(variant.price || 0).toFixed(2)}
                    </p>
                    {/* Variant size selector */}
                    {family?.variant_order?.length > 1 && (
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {family.variant_order.map((vid) => {
                          const v = family.variants[vid];
                          const active = String(vid) === String(selectedId);
                          return (
                            <button
                              key={vid}
                              type="button"
                              className={`btn btn-sm ${active ? "btn-primary" : "btn-outline-primary"}`}
                              onClick={() => setSelectedId(String(vid))}
                              title={`$${Number(v.price || 0).toFixed(2)}`}
                            >
                              {v.product_size || "Variant"}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {/* Quantity */}
                    <div className="mt-4">
                      <label className="form-label fw-bold small text-muted">Quantity</label>
                      <div className="d-flex align-items-center">
                        <button className="btn btn-outline-secondary" onClick={decrement}>−</button>
                        <div className="px-3">{quantity}</div>
                        <button className="btn btn-outline-secondary" onClick={increment}>+</button>
                      </div>
                    </div>
                    <div className="mt-4 d-flex gap-3 flex-column flex-sm-row">
                      <button className="btn btn-dark d-flex align-items-center justify-content-center gap-2" onClick={handleAddToCart}>
                        Add to cart
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary d-flex align-items-center justify-content-center gap-2 pointer"
                        onClick={() => navigate("/shipping-info")}
                      >
                        Checkout
                      </button>
                      {/* Shop on Amazon button under price */}
                      {variant?.amazon_url && (
                        <div className="">
                          <a
                            href={variant.amazon_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-warning d-flex align-items-center justify-content-center gap-2 pointer"
                          >
                            Buy on Amazon
                          </a>
                        </div>
                      )}
                    </div>

                    {/* HTML Description */}
                    <div
                      className="pt-3 text-muted"
                      dangerouslySetInnerHTML={{ __html: descriptionHTML }}
                    />

                    <PromoOffer
                      open={promoOpen}
                      onOpenChange={setPromoOpen}
                      tabText="Get 20% Off"
                      imageAlt="New customer discount"
                    />
                  </div>
                </div>
              </section>
            </div>
          </div>
        </section>

        {/* Lightbox for zoom/pan */}
        <Lightbox
          open={lbOpen}
          close={() => setLbOpen(false)}
          index={lbIndex}
          slides={lightboxSlides}
          plugins={[Zoom]}

          // 1) translucent background (60% black)
          styles={{ 
            container: { backgroundColor: "rgba(0, 0, 0, 0.6)" } 
            // or: root: { "--yarl__color_backdrop": "rgba(0,0,0,.6)" }
          }}

          // 2) click anywhere on the backdrop to close
          controller={{ closeOnBackdropClick: true }}
        />
      </Layout>
    </div>
  );
};

export default ProductDetail;
